/**
 * #480 Lever 3 — thin preflight an operator can run BEFORE updating the harness.
 *
 * Levers 1 and 2 are both reactive. Lever 1 notices, server-side, that an agent
 * went quiet. Lever 2 prints one clear line once the harness has already
 * drifted. Neither runs at the only moment the operator can still decide not to
 * update — which is what this is for.
 *
 * Everything here is a PURE function over already-collected inputs. `doctor.ts`
 * owns the I/O (execSync, config reads); this module owns the judgement. That
 * split is deliberate: `src/__tests__/doctor.test.ts` has been excluded from the
 * suite since v0.6.11 because its child_process mocks drifted, so anything added
 * to doctor.ts directly ships without a test gate. Keeping the decisions in a
 * leaf module means the part that can be WRONG stays covered.
 *
 * Inherits Lever 2's restraint rules: never throw, never block, and treat
 * UNKNOWN as skip rather than as failure.
 */

import { compareVersions, MIN_OPENCLAW_VERSION, TESTED_THROUGH_OPENCLAW_VERSION } from "./harness-compat.js";
import type { HarnessCompat } from "./harness-compat.js";
import type { CheckResult } from "./doctor.js";

/**
 * Render a Lever 2 compat verdict as a doctor check.
 *
 * Note the deliberate divergence from Lever 2's reporting: the plugin stays
 * SILENT when healthy (a line on every boot is a line nobody reads), but doctor
 * must show the healthy case. A preflight answers "am I about to leave the
 * supported range?", and that question needs the range on screen even when the
 * current answer is no.
 */
export function describeHarnessCompat(compat: HarnessCompat | null): CheckResult {
  const name = "Harness version supported";
  try {
    if (!compat) {
      return {
        name,
        status: "skip",
        message:
          "OpenClaw version could not be resolved from the plugin's install path — " +
          "no verdict (an unrecognised layout is not a fault).",
      };
    }

    const range = `supported ${MIN_OPENCLAW_VERSION} – ${TESTED_THROUGH_OPENCLAW_VERSION}`;

    if (compat.verdict === "too-old") {
      return {
        name,
        status: "fail",
        message:
          `OpenClaw ${compat.version} is below the minimum this plugin supports ` +
          `(${MIN_OPENCLAW_VERSION}). AgentVault still loads, but errors you see may be ` +
          `version drift rather than configuration faults. ${range}.`,
      };
    }

    if (compat.verdict === "untested") {
      // Not "broken" — untested. Failing here would make every OpenClaw release
      // day look like an AgentVault outage, which is how a preflight earns the
      // reputation that gets it ignored.
      return {
        name,
        status: "warn",
        message:
          `OpenClaw ${compat.version} is newer than the version this plugin was tested ` +
          `against (${TESTED_THROUGH_OPENCLAW_VERSION}). Proceeding is expected to work — ` +
          `report anything that misbehaves. ${range}.`,
      };
    }

    if (compat.verdict === "ok") {
      return { name, status: "pass", message: `OpenClaw ${compat.version} (${range})` };
    }

    // Unrecognised verdict — a future Lever 2 value we do not know how to render.
    return { name, status: "skip", message: `Unrecognised compat verdict for ${compat.version}` };
  } catch {
    return { name, status: "skip", message: "Compat check unavailable" };
  }
}

/**
 * Compare the OpenClaw the OPERATOR drives against the OpenClaw the PLUGIN loads.
 *
 * `openclaw --version` reports whatever is first on PATH. The plugin runs against
 * whatever `openclaw/package.json` resolves to from its own install directory —
 * and on a real gateway that directory is a hashed `g-*` path that changes on
 * every plugin update. When those two diverge, the operator is reading logs from
 * a gateway that is not the one their plugin bound to, and no amount of log
 * detail makes that visible. It is the #480 incident shape, caught early.
 *
 * WARN rather than FAIL: a split layout can be legitimate, and a preflight that
 * false-fails on a working install is worse than one that says nothing.
 */
export function checkHarnessDrift(
  cliVersion: string | null,
  moduleVersion: string | null,
): CheckResult {
  const name = "Harness identity (CLI vs plugin)";
  try {
    const cli = cliVersion?.trim();
    const mod = moduleVersion?.trim();

    // A missing side is not a mismatch. Rule 4 again.
    if (!cli || !mod) {
      return {
        name,
        status: "skip",
        message: "Only one of the CLI / resolved-module versions is known — nothing to compare.",
      };
    }

    // Compare by value: "2026.6" and "2026.6.0" are the same release, and
    // flagging them would be a false positive on a common layout.
    if (compareVersions(cli, mod) === 0) {
      return { name, status: "pass", message: `CLI and plugin both see OpenClaw ${cli}` };
    }

    return {
      name,
      status: "warn",
      message:
        `'openclaw --version' reports ${cli}, but the plugin resolves OpenClaw ${mod}. ` +
        `You may be driving one gateway while AgentVault is bound to another — ` +
        `check for a second install before updating.`,
    };
  } catch {
    return { name, status: "skip", message: "Drift check unavailable" };
  }
}

/**
 * Pull a dotted-numeric version out of `openclaw --version` output.
 *
 * Observed live on 2026-08-05: `OpenClaw 2026.7.1 (2d2ddc4)` — a name and a git
 * sha around the number. Handing that string straight to compareVersions parses
 * "OpenClaw 2026" as NaN -> 0 and yields 0.7.1, which reports drift on an
 * install that is actually fine. Requiring every segment to be numeric also
 * stops a sha from being mistaken for a version.
 *
 * Returns null when there is nothing version-shaped — skip beats invention.
 */
export function parseCliVersion(raw: string | null): string | null {
  try {
    if (!raw) return null;
    const match = raw.match(/\bv?(\d+(?:\.\d+)+)\b/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Report which OpenClaw SDK capabilities actually resolved.
 *
 * Every SDK wrapper degrades to a no-op when its symbol cannot be found, which
 * is right for resilience and terrible for visibility: three capabilities were
 * dead on OpenClaw 2026.7.1 for weeks and produced no error, no log line, and
 * no failing test. `sdk-capability-gate.test.ts` catches that locally; this is
 * the same signal on a customer's box, where nobody runs our test suite.
 *
 * WARN, not FAIL — the plugin's core messaging path does not depend on these,
 * so a dead auxiliary must not read as "AgentVault is broken".
 */
export function describeSdkCapabilities(
  results: Array<{ name: string; resolved: boolean }>,
): CheckResult {
  const name = "OpenClaw SDK capabilities";
  try {
    if (!results.length) {
      return { name, status: "skip", message: "No capabilities probed" };
    }
    const dead = results.filter((r) => !r.resolved).map((r) => r.name);
    if (dead.length === 0) {
      return {
        name,
        status: "pass",
        message: `${results.length}/${results.length} resolved (${results.map((r) => r.name).join(", ")})`,
      };
    }
    return {
      name,
      status: "warn",
      message:
        `${results.length - dead.length}/${results.length} resolved — unavailable: ${dead.join(", ")}. ` +
        `Core messaging is unaffected, but these degrade silently, so this line ` +
        `is the only place they are visible. Usually means the harness moved or ` +
        `renamed an SDK export.`,
    };
  } catch {
    return { name, status: "skip", message: "Capability probe unavailable" };
  }
}

/** Report the AgentVault plugin's own version, so a bug report can name both sides. */
export function describePluginVersion(version: string | null): CheckResult {
  const name = "AgentVault plugin version";
  try {
    if (!version) {
      return { name, status: "skip", message: "Plugin version could not be resolved" };
    }
    return { name, status: "pass", message: version };
  } catch {
    return { name, status: "skip", message: "Plugin version unavailable" };
  }
}
